import { useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type { CatalogCategoryKindType, DirCatalogCategoryType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import { useSettings } from 'context/SettingsContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import { CATALOG_DIR_I18N_NS } from './model/catalogDirApi';
import type { CatalogSelectOption } from './types';

interface CatalogCategoryFilterProps {
    kind: CatalogCategoryKindType;
    filterCategoryIds: number[];
    setFilterCategoryIds: (ids: number[]) => void;
    embedded?: boolean;
    multiple?: boolean;
}

const DIRS_KEY: Record<CatalogCategoryKindType, 'ProductCategory' | 'ProductSetCategory' | 'ServiceCategory'> = {
    product: 'ProductCategory',
    set: 'ProductSetCategory',
    service: 'ServiceCategory',
};

const CatalogCategoryFilterSelect = ({
    kind,
    filterCategoryIds,
    setFilterCategoryIds,
    multiple = true,
}: Omit<CatalogCategoryFilterProps, 'embedded'>) => {
    const ns = CATALOG_DIR_I18N_NS[kind];
    const { t } = useTranslation([ns, 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const categoryOptions = useMemo<CatalogSelectOption[]>(
        () =>
            ((localSettings?.DIRS?.[DIRS_KEY[kind]] || []) as DirCatalogCategoryType[])
                .filter((item) => !item.hidden || filterCategoryIds.includes(item.ID))
                .map((item) => ({ value: item.ID, label: item.categoryName })),
        [kind, localSettings?.DIRS, filterCategoryIds],
    );

    const selected = categoryOptions.filter((opt) => filterCategoryIds.includes(Number(opt.value)));
    const selectStyles = {
        ...selectTheme,
        menuPortal: (base: object) => ({ ...base, zIndex: 2100 }),
    };
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;

    return (
        <>
            <label className="form-label text-muted fs-12 mb-1">{t('filterCategory')}</label>
            {multiple ? (
                <Select<CatalogSelectOption, true>
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={categoryOptions}
                    value={selected}
                    onChange={(opts) => setFilterCategoryIds((opts || []).map((opt) => Number(opt.value)))}
                    placeholder={t('filterCategory')}
                    className="w-100"
                    styles={selectStyles}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                />
            ) : (
                <Select<CatalogSelectOption, false>
                    isClearable
                    options={categoryOptions}
                    value={selected[0] || null}
                    onChange={(opt) => setFilterCategoryIds(opt?.value ? [Number(opt.value)] : [])}
                    placeholder={t('filterCategory')}
                    className="w-100"
                    styles={selectStyles}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                />
            )}
        </>
    );
};

const CatalogCategoryFilter = ({
    kind,
    filterCategoryIds,
    setFilterCategoryIds,
    embedded = false,
    multiple = true,
}: CatalogCategoryFilterProps) => {
    const ns = CATALOG_DIR_I18N_NS[kind];
    const { t } = useTranslation([ns, 'common']);
    const [show, setShow] = useState(false);
    const [draftCategoryIds, setDraftCategoryIds] = useState<number[]>(filterCategoryIds);
    const filterActive = filterCategoryIds.length > 0;

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftCategoryIds([...filterCategoryIds]);
        setShow(true);
    };

    if (embedded) {
        return (
            <div className="mb-3">
                <CatalogCategoryFilterSelect
                    kind={kind}
                    filterCategoryIds={filterCategoryIds}
                    setFilterCategoryIds={setFilterCategoryIds}
                    multiple={multiple}
                />
            </div>
        );
    }

    return (
        <>
            <EToolTip title={t('toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <CatalogCategoryFilterSelect
                                    kind={kind}
                                    filterCategoryIds={draftCategoryIds}
                                    setFilterCategoryIds={setDraftCategoryIds}
                                    multiple={multiple}
                                />
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setFilterCategoryIds([]);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px"
                                    onClick={() => {
                                        setFilterCategoryIds([...draftCategoryIds]);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default CatalogCategoryFilter;
